import { getApolloClient } from './graphQL/apolloClient';
import {
  SET_TRIGGER_SETTINGS,
  QUERY_TRIGGER_SETTINGS,
  DISCONNECT_SERVICE_PROVIDER
} from './graphQL/thermostats/queries';
import { ThermostatsActions } from '../store/actionCreators';

export const getOS = userAgent => {
  const clientStrings = [
    { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
    { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
    { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
    { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
    { s: 'Windows Vista', r: /Windows NT 6.0/ },
    { s: 'Windows Server 2003', r: /Windows NT 5.2/ },
    { s: 'Windows XP', r: /(Windows NT 5.1|Windows XP)/ },
    { s: 'Windows 2000', r: /(Windows NT 5.0|Windows 2000)/ },
    { s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
    { s: 'Windows 98', r: /(Windows 98|Win98)/ },
    { s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
    { s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
    { s: 'Windows CE', r: /Windows CE/ },
    { s: 'Windows 3.11', r: /Win16/ },
    { s: 'Android', r: /Android/ },
    { s: 'Open BSD', r: /OpenBSD/ },
    { s: 'Sun OS', r: /SunOS/ },
    { s: 'Linux', r: /(Linux|X11)/ },
    { s: 'iOS', r: /(iPhone|iPad|iPod)/ },
    { s: 'Mac OS X', r: /Mac OS X/ },
    { s: 'Mac OS', r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
    { s: 'QNX', r: /QNX/ },
    { s: 'UNIX', r: /UNIX/ },
    { s: 'BeOS', r: /BeOS/ },
    { s: 'OS/2', r: /OS\/2/ },
    { s: 'Search Bot', r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/ }
  ];

  return clientStrings.find(clientString => clientString.r.test(userAgent)).s;
};

export const VALUE_INDEX_MAP = {
  co2: [0, 500, 1000, 1500, 2000],
  voc_v1: [0, 250, 500, 750, 1000],
  voc: [0, 500, 1000, 1500, 2000],
  dust: [0, 15, 30, 45, 60],
  pm25: [0, 20, 40, 60, 80]
};

export const DEFAULT_VALUE = {
  co2: 600,
  voc_v1: 350,
  voc: 330,
  dust: 8,
  pm25: 15
};

export const getDeviceTypeComponents = deviceType => {
  const deviceTypeSensorMap = {
    awair: ['co2', 'voc_v1', 'dust'],
    'awair-r2': ['co2', 'voc', 'pm25'],
    'awair-omni': ['co2', 'voc', 'pm25'],
    'awair-mint': ['voc', 'pm25'],
    'awair-glow': ['co2', 'voc_v1'],
    'awair-glow-c': ['voc']
  };

  return deviceTypeSensorMap[deviceType] || ['co2', 'voc', 'pm25'];
};

export const getInitialTriggerSettings = (triggerSettings, sensorComponents, isV1) => {
  let isVocV1;
  return sensorComponents.reduce((accumulator, component) => {
    const enabledTriggerSetting = triggerSettings.find(triggerSetting => triggerSetting.comp === component);
    isVocV1 = false;
    if (component === 'voc' && isV1) {
      isVocV1 = true;
    }
    return {
      ...accumulator,
      [component]: {
        isEnabled: !!enabledTriggerSetting,
        onAt:
          enabledTriggerSetting != null
            ? enabledTriggerSetting.onAt
            : isVocV1
            ? DEFAULT_VALUE['voc_v1']
            : DEFAULT_VALUE[component],
        // Add triggerUuid entry if enabledTriggerSetting exists
        ...(enabledTriggerSetting != null && { triggerUuid: enabledTriggerSetting.triggerUuid })
      }
    };
  }, {});
};

export const initializeTriggerEntities = (triggerEntities, deviceType) => {
  const changedTriggerEntities = JSON.parse(JSON.stringify(triggerEntities));

  for (const key in changedTriggerEntities) {
    changedTriggerEntities[key].isEnabled = false;
    if (deviceType === 'awair' && key === 'voc') {
      changedTriggerEntities[key].onAt = DEFAULT_VALUE['voc_v1'];
    } else {
      changedTriggerEntities[key].onAt = DEFAULT_VALUE[key];
    }
  }
  return changedTriggerEntities;
};

export const getInitialTriggerSettingsForThermostats = (triggerSettings, sensorComponents, isV1) => {
  let isVocV1;
  const { triggers } = triggerSettings;
  return sensorComponents.reduce((accumulator, component) => {
    const enabledTriggerSetting = triggers.find(trigger => trigger.settings.sensorType.toLowerCase() === component);
    isVocV1 = false;
    if (component === 'voc' && isV1) {
      isVocV1 = true;
    }
    return {
      ...accumulator,
      [component]: {
        isEnabled: !!enabledTriggerSetting,
        onAt:
          enabledTriggerSetting != null
            ? enabledTriggerSetting.settings.threshold
            : isVocV1
            ? DEFAULT_VALUE['voc_v1']
            : DEFAULT_VALUE[component]
      }
    };
  }, {});
};

export const setSettingTriggers = (deviceUuid, deviceType, action, targetId, triggerSettingsList) => {
  const triggerGroup = {
    deviceUuid,
    action,
    targetId
  };
  getApolloClient()
    .mutate({
      mutation: SET_TRIGGER_SETTINGS,
      variables: {
        triggerGroup,
        triggerSettingsList
      }
    })
    .then(res => {
      getApolloClient()
        .query({
          query: QUERY_TRIGGER_SETTINGS,
          variables: {
            deviceUuid,
            action,
            targetId
          },
          fetchPolicy: 'network-only'
        })
        .then(response => {
          ThermostatsActions.unsetLoading();
          let isV1 = false;
          // Retrieve trigger settings to update triggerIds
          const triggerSettings = response.data.getTriggers;
          const sensorComponents = getDeviceTypeComponents(deviceType);
          const forInitialSensorComponents = sensorComponents.map(sensor => {
            if (sensor === 'voc_v1') {
              isV1 = true;
              return 'voc';
            }
            return sensor;
          });
          const initialTriggerEntities = getInitialTriggerSettingsForThermostats(triggerSettings, forInitialSensorComponents, isV1);

          // Reset trigger entities to the updated values to prepare for next trigger setting/s
          ThermostatsActions.setInitialTriggerEntities(initialTriggerEntities);
          ThermostatsActions.setChangedTriggerEntities(initialTriggerEntities);
          ThermostatsActions.convertToInitialValue(false);
          ThermostatsActions.hideCompleteModal({ isClosed: false, text: 'Saved!' });
        })
        .catch(error => {
          ThermostatsActions.unsetLoading();
          alert('Failed to retrieve settings.\nPlease try again later.');
        });
    })
    .catch(error => {
      ThermostatsActions.unsetLoading();
      alert('Failed to set notification.\nPlease try again later.');
    });
};

export const disconnectServiceProvider = (deviceUuid, isEcobee) => {
  getApolloClient()
    .mutate({
      mutation: DISCONNECT_SERVICE_PROVIDER,
      variables: {
        name: isEcobee ? 'ecobeehome' : 'sensi',
        deviceUuid
      }
    })
    .then(() => {
      // Won't work on android if os of core store is used
      const OS = getOS(window.navigator.userAgent);

      if (OS === 'Android') {
        window.android && window.android.dismiss();
      } else if (OS === 'iOS') {
        window.location.replace('awair://dismiss');
      }
    })
    .catch(error => {
      ThermostatsActions.unsetLoading();
      alert(`Failed to disconnect ${isEcobee ? 'Ecobee' : 'Sensi'} from Awair.\nPlease try again later.`);
    });
};



// WEBPACK FOOTER //
// ./src/lib/functions.js